import modelExtend from 'dva-model-extend';
import deepmerge from 'deepmerge';
import { createSelector } from 'reselect';

import childModel, {
  ChildFields,
  DeadSecondParent,
  DivorcedSecondParent,
  SecondParent,
} from './child';
import { addChildModalOpen } from './modals';
import getFullName from '../helpers/getFullName';
import { hasErrors } from '../validators';
import {
  getActiveChildSecondParent,
  getActiveChildSecondParentStatusValue,
  validateActiveChildFromRelativesPassportStatus,
} from '../selectors/child';
import { SECOND_PARENT_STATUS } from '../constants/secondParentStatus';
import { getApplicantIsAddressCompleted } from '../selectors/applicant';
import { getChildrenError, validateChildren } from '../validators/children';
import {
  getChildren,
  getNotHiddenChildren,
  getNotHiddenChildrenIds,
  getChildrenObject,
} from '../selectors/children';
import { ApplicantFields } from './applicant';
import { Field } from '../declarations/widget-ums';
import { validateCards, getChildrenToUpdate } from '../validators/card';
import { createField } from '../helpers/fieldHelpers';
import { initialAddressInfo } from './address';
import { ERROR_DATE } from '../constants/errors';
import { spouseAddressComplete } from '../selectors/spouse';
import { getId } from '../helpers/getIds';
import {
  initialChildState,
  initialOrganizationState,
  initialSecondParentState,
} from '../helpers/initialState';
import {
  differenceObject,
  getDulDifferenceObject,
} from '../helpers/differenceObject';
import { dulInitialState } from '../constants/document';
import { getElkChildren } from '../selectors/elk';
import { CHILD_ADDRESS, CONFIRMING_DOCUMENTS } from '../constants/child';
import {
  isUnder23YearsOld,
  shouldBeGettingEducation,
} from '../helpers/dateHelpers';
import { CHILDREN } from '../constants/modelNames';
import { passportIssueDateValidation } from '../validators';

export const isChildrenStepDisabled: (state: {
  applicant: ApplicantFields;
  children: ChildFields[];
}) => boolean = createSelector(
  getApplicantIsAddressCompleted,
  spouseAddressComplete,
  (isAddressCompleted, spouseAddressComplete) => {
    return !(isAddressCompleted && spouseAddressComplete);
  }
);

export const getSecondParentsWithSameStatus: (state: {
  child: ChildFields;
  children: ChildFields[];
}) => SecondParent[] = createSelector(
  getChildren,
  getActiveChildSecondParentStatusValue,
  (children, status): SecondParent[] => {
    if (
      [SECOND_PARENT_STATUS.single, SECOND_PARENT_STATUS.paternity].includes(
        status
      ) ||
      !children.length
    ) {
      return [];
    }
    const secondParents = children
      .filter(
        (
          {
            secondParent: {
              status: { value },
              id,
            },
          },
          index,
          arr
        ) =>
          value === status &&
          arr.findIndex(
            ({ secondParent: { id: secondId } }) => secondId === id
          ) === index
      )
      .map(({ secondParent }) => {
        const text = getFullName(secondParent);
        return { ...secondParent, text, value: text };
      });

    return secondParents;
  }
);

export const secondParentDeadSelector: (state: {
  child: ChildFields;
  children: ChildFields[];
}) => {
  birthdate: Field<string>;
  showTitle: boolean;
  deathdate: Field<string>;
  proofOfDeath: Field<string>;
  deathDocIssuer: Field<string>;
} = createSelector(
  getActiveChildSecondParent,
  getSecondParentsWithSameStatus,
  (
    { birthdate, deathdate, proofOfDeath, deathDocIssuer }: DeadSecondParent,
    secondParentsWithSameStatus
  ) => ({
    birthdate,
    deathdate,
    proofOfDeath,
    deathDocIssuer,
    showTitle: !secondParentsWithSameStatus.length,
  })
);

export const secondParentDivorcedSelector: (state: {
  child: ChildFields;
  children: ChildFields[];
}) => {
  divorceDocIssuer: Field<string>;
  showTitle: boolean;
  dateOfDivorce: Field<string>;
  dateOfMarriage: Field<string>;
} = createSelector(
  getActiveChildSecondParent,
  getSecondParentsWithSameStatus,
  (
    { dateOfMarriage, dateOfDivorce, divorceDocIssuer }: DivorcedSecondParent,
    secondParentsWithSameStatusArray
  ) => ({
    dateOfMarriage: {
      ...dateOfMarriage,
      error:
        dateOfMarriage.error ||
        (!!dateOfMarriage.value &&
          dateOfDivorce.error === ERROR_DATE.DIVORCE_DATE_EARLY_MARRIAGE),
    },
    dateOfDivorce: {
      ...dateOfDivorce,
      error:
        dateOfDivorce.error ||
        (!!dateOfDivorce.value &&
          dateOfMarriage.error === ERROR_DATE.DIVORCE_DATE_EARLY_MARRIAGE),
    },
    divorceDocIssuer,
    showTitle: !secondParentsWithSameStatusArray.length,
  })
);

const childrenModel = modelExtend({
  namespace: 'children',
  state: {},
  reducers: {
    reset() {
      return {};
    },
    'save/bulk'(state, { payload }) {
      return Object.assign(state, payload);
    },
    delete(state, { payload }) {
      delete state[payload];
      return state;
    },
    'set/error'(state, { payload }) {
      const { children, cardsValidation } = payload;
      return deepmerge(children, cardsValidation);
    },
    'cardStatus/update'(state, { payload }) {
      for (const key in payload) {
        if (payload.hasOwnProperty(key)) {
          state[key].cardStatus = payload[key].cardStatus;
        }
      }
      return state;
    },
    'card/highlight'(state, { payload }) {
      for (const key in state) {
        if (
          state.hasOwnProperty(key) &&
          !state[key].cardStatus.isCardCompleted
        ) {
          state[key].cardStatus.highlight = payload;
        }
      }
      return state;
    },
    'reset/address'(state, { payload }) {
      for (const key in payload) {
        if (payload.hasOwnProperty(key)) {
          const { id } = payload[key];
          state[id].address = initialAddressInfo;
          state[id].addressMatchWithApplicant = createField();
          state[id].isRegisteredInMoscow = true;
        }
      }
      return state;
    },
    'reset/parents'(state, { payload }) {
      for (const key in payload) {
        if (payload.hasOwnProperty(key)) {
          const { id } = payload[key];
          state[id].bornInCurrentMarriage = createField();
          state[id].bornInNotCurrentMarriage = createField();
          state[id].secondParent = Object.assign(
            {},
            { ...initialSecondParentState, id: getId() }
          );
          if (
            state[id].confirmingDocs.value === CONFIRMING_DOCUMENTS.indirectly
          ) {
            state[id].confirmingDocs = createField();
            state[id].education = initialOrganizationState;
            state[id].polyclinic = initialOrganizationState;
          }
        }
      }
      return state;
    },
    'update/addressMatchWithApplicant'(state, { payload }) {
      for (const key in payload) {
        if (payload.hasOwnProperty(key)) {
          const { id } = payload[key];
          state[id].addressMatchWithApplicant.value =
            CHILD_ADDRESS.matchWithApplicant;
        }
      }
      return state;
    },
    'toggle/isRegisteredInMoscow'(state, { payload }) {
      for (const key in payload) {
        if (payload.hasOwnProperty(key)) {
          const { id } = payload[key];
          state[id].address = initialAddressInfo;
          state[id].isRegisteredInMoscow = true;
        }
      }
      return state;
    },
    'set/passport/status'(state, { payload }) {
      for (const key in payload) {
        if (payload.hasOwnProperty(key)) {
          const { id } = payload[key];
          state[id].dul.status = payload[key].status;
        }
      }
      return state;
    },
  },
  effects: {
    *'toggle/isRegisteredInMoscow'(
      { type, payload: isRegisteredInMoscow },
      { put, select }
    ) {
      const { address: childrenWithAddressEqualSpouse } = yield select(
        getChildrenToUpdate
      );

      if (childrenWithAddressEqualSpouse.length) {
        const payload = childrenWithAddressEqualSpouse.reduce((acc, child) => {
          child.address = initialChildState;
          child.isRegisteredInMoscow = isRegisteredInMoscow;
          acc[child.id] = child;
          return acc;
        }, {});
        yield put({ type: 'save/bulk', payload });
      }
      yield put({ type: 'form/saveDraft' });
    },
    watcher: [
      function* ({ take, put, select }) {
        yield take('elk/getBlocks/finish');
        const children = yield select(getElkChildren);

        yield put({ type: 'save/bulk', payload: children });
      },
      { type: 'watcher' },
    ],
    formErrorWatcher: [
      function* ({ take, put }) {
        while (true) {
          const {
            payload: { children },
          } = yield take('form/set/error');
          if (hasErrors(children)) {
            const error = getChildrenError(children);
            const type = 'children';

            yield put({
              type: 'steps/set/error',
              payload: { error, type },
            });
          }
        }
      },
      { type: 'watcher' },
    ],
    applicantStatusWatcher: [
      function* ({ take, put, select }) {
        while (true) {
          const actions = [
            'applicant/maritalStatus/toggle',
            'applicant/livingTogether/toggle',
          ];
          yield take(actions);
          const getActiveChildren = yield select(getNotHiddenChildrenIds);
          if (!getActiveChildren.length) {
            yield put({ type: 'form/saveDraft' });
            continue;
          }

          const childrenToUpdate = yield select(getChildrenToUpdate);
          if (!childrenToUpdate) {
            yield put({ type: 'form/saveDraft' });
            continue;
          }

          if (childrenToUpdate.parents.length) {
            yield put({
              type: 'reset/parents',
              payload: childrenToUpdate.parents,
            });
          }

          if (childrenToUpdate.address.length) {
            yield put({
              type: 'reset/address',
              payload: childrenToUpdate.address,
            });
          }
          const cardsValidation = yield select(validateCards);
          yield put({
            type: 'cardStatus/update',
            payload: cardsValidation,
          });
          yield put({ type: 'form/saveDraft' });
        }
      },
      { type: 'watcher' },
    ],
    *save({ payload, fromDraft = false }, { put }) {
      const { id = '' } = payload;
      if (id) {
        const { birthdate, confirmingDocs } = payload;
        if (
          [
            !isUnder23YearsOld(birthdate.value),
            !shouldBeGettingEducation(birthdate.value) &&
              confirmingDocs.value !== CONFIRMING_DOCUMENTS.indirectly,
          ].some(Boolean)
        ) {
          payload.education = { ...initialOrganizationState };
          payload.polyclinic = { ...initialOrganizationState };
          payload.isStudying = initialChildState.isStudying;
        }
        const child = { [id]: payload };
        yield put({ type: 'save/bulk', payload: child });
        yield put({ type: 'secondParent/update', payload });
      }
      if (!fromDraft) {
        yield put({ type: 'form/saveDraft' });
      }
    },
    *edit({ payload }, { select, put }) {
      const children = yield select((state) => state.children);

      const child = children[payload] || childModel.state;

      yield put({ type: 'child/edit', payload: child });

      const childPassportStatus = yield select(
        validateActiveChildFromRelativesPassportStatus
      );

      yield put({
        type: 'child/set/passport/status',
        payload: childPassportStatus,
      });
      yield put(addChildModalOpen(true));
    },
    *delete({ payload }, { select, put }) {
      if (/^ums/i.test(payload)) return;
      const children = yield select(getElkChildren);
      if (payload) {
        yield put({
          type: 'save/bulk',
          payload: { [payload]: children[payload] },
        });
      }
    },
    *validate(_, { select, put }) {
      const childrenHaveErrors = yield select((state) =>
        validateChildren(state)
      );

      if (!childrenHaveErrors) {
        yield put({ type: 'steps/set/completed', payload: 'children' });
      }
      const childrenError = yield select((state) => state.steps.children.error);
      if (childrenError) {
        yield put({
          type: 'steps/check/error',
          payload: { type: 'children', validatorName: childrenError },
        });
      }
    },
    *'cards/validate'({ payload = false }, { select, put }) {
      const cardsValidation = yield select(validateCards);
      yield put({
        type: 'cardStatus/update',
        payload: cardsValidation,
      });
      if (payload) {
        yield put({
          type: 'form/set/readyNamespace',
          payload: CHILDREN,
        });
      }
    },
    *'secondParent/update'({ payload }, { select, put }) {
      const {
        id: childId,
        secondParent: { id: secondParentId },
      } = payload;
      const newChild = { [childId]: payload };

      const children = yield select(getNotHiddenChildren);

      if (children.length) {
        const newPayload = children
          .filter(({ secondParent: { id } }) => id === secondParentId)
          .reduce(
            (next, child) => ({
              ...next,
              [child.id]: {
                ...child,
                secondParent: payload.secondParent,
                selectedParent: payload.selectedParent,
              },
            }),
            newChild
          );
        yield put({
          type: 'cards/validate',
          payload: true,
        });
        yield put({ type: 'save/bulk', payload: newPayload });
      }
    },
    draftWatcher: [
      function* ({ take, put, select, call }) {
        function* compareChildren(children, elkChildren) {
          for (const child of children) {
            const equalChildData = elkChildren[child.id];

            if (equalChildData) {
              const {
                dul: childDul,
                birthCertificate: childBC,
                address,
                id: childId,
                secondParent,
                isStudying,
                isRegisteredInMoscow,
                ...person
              } = child;
              const {
                dul: elkDul,
                birthCertificate: elkBC,
                id,
                ...rest
              } = equalChildData;
              const childFields = differenceObject(rest, person);
              const dul = getDulDifferenceObject(elkDul, childDul);
              const birthCertificate = getDulDifferenceObject(elkBC, childBC);
              const resultChild = {
                ...childFields,
                fullName: equalChildData.fullName,
                id,
                dul,
                birthCertificate,
                address,
                secondParent,
                isStudying,
                isRegisteredInMoscow,
                hidden: false,
              };
              const mergedChild = deepmerge(child, resultChild);
              yield put({
                type: 'save',
                payload: mergedChild,
                fromDraft: true,
              });
              yield put({
                type: 'notHiddenChildren/add',
                payload: equalChildData.id,
              });
              yield put({ type: 'secondParent/update', payload: mergedChild });
              continue;
            }
            const fullName = getFullName(child);
            const newChild = {
              ...deepmerge(initialChildState, child),
              dul: child.dul || dulInitialState,
              fullName,
              isRegisteredInMoscow: child.isRegisteredInMoscow,
              hidden: false,
            };

            yield put({
              type: 'save/bulk',
              payload: { [child.id]: newChild },
            });

            yield put({
              type: 'notHiddenChildren/add',
              payload: child.id,
            });
            yield put({ type: 'secondParent/update', payload: newChild });
          }
        }
        while (true) {
          const {
            payload: { children },
          } = yield take('form/set/draftData');

          const elkChildren = yield select(getChildrenObject);
          yield compareChildren(children, elkChildren);

          yield put({
            type: 'passport/validity',
          });

          yield put({
            type: 'cards/validate',
            payload: true,
          });
        }
      },
      { type: 'watcher' },
    ],
    formResetWatcher: [
      function* ({ take, put, select }) {
        while (true) {
          yield take('form/reset');
          yield put({ type: 'reset' });
          yield put({ type: 'notHiddenChildren/reset' });
          const children = yield select(getElkChildren);
          yield put({ type: 'save/bulk', payload: children });
        }
      },
      { type: 'watcher' },
    ],
    *'check/update/addressMatchWithApplicant'(_, { put, select }) {
      const childrenToUpdate = yield select(getChildrenToUpdate);
      if (!(childrenToUpdate || childrenToUpdate.address.length)) return;

      yield put({
        type: 'update/addressMatchWithApplicant',
        payload: childrenToUpdate.address,
      });
    },
    *'passport/validity'({ type }, { select, put }) {
      const [modelNamespace] = type.split('/');

      const validation = passportIssueDateValidation[modelNamespace];
      const passportStatus = yield select(validation);

      yield put({
        type: 'set/passport/status',
        payload: passportStatus,
      });
    },
  },
});

export default childrenModel;
